import { useLayoutEffect, useRef, type ReactNode } from "react"
import { cn } from "@/lib/utils"
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "../Table/Table"

/** Over this many rows the table scrolls, showing the header and half of the last row in view. */
const MAX_FULL_ROWS = 8
const NUMERIC = /^[+-]?\$?\d[\d,]*(\.\d+)?%?$/

export interface ChatTableProps {
  header: string[]
  rows: string[][]
  renderCell?: (text: string) => ReactNode
  className?: string
}

export function ChatTable({ header, rows, renderCell = (text) => text, className }: ChatTableProps) {
  // Numbers right-aligned, except the first column, which labels the row.
  const right = header.map((_, c) => c > 0 && rows.length > 0 && rows.every((r) => NUMERIC.test((r[c] ?? "").trim())))
  const boxRef = useRef<HTMLDivElement>(null)
  const long = rows.length > MAX_FULL_ROWS

  useLayoutEffect(() => {
    const box = boxRef.current
    if (!box) return
    const head = box.querySelector<HTMLElement>("thead tr")
    const first = box.querySelector<HTMLElement>("tbody tr")
    const scrollbar = box.offsetHeight - box.clientHeight - 2 * box.clientTop
    box.style.maxHeight =
      long && head && first ? `${head.offsetHeight + (MAX_FULL_ROWS - 0.5) * first.offsetHeight + scrollbar}px` : ""
  }, [long, rows.length])

  return (
    <div
      ref={boxRef}
      data-testid="chat-table"
      className={cn("my-2 overflow-auto rounded-md border border-border", className)}
    >
      <table className="w-full caption-bottom text-sm">
        <TableHeader>
          <TableRow className="sticky top-0 z-10 border-b border-border bg-muted hover:bg-muted">
            {header.map((h, c) => (
              <TableHead
                key={c}
                className={cn(
                  "h-auto px-3 py-1.5 text-caption tracking-wide whitespace-nowrap text-muted-foreground uppercase",
                  right[c] ? "text-right" : "text-left",
                )}
              >
                {h}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i} className="border-b border-border last:border-0 hover:bg-transparent">
              {header.map((_, c) => (
                <TableCell
                  key={c}
                  className={cn(
                    "px-3 py-1.5 text-body font-normal whitespace-nowrap text-foreground",
                    right[c] && "text-right tabular-nums",
                  )}
                >
                  {renderCell(r[c] ?? "")}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </table>
    </div>
  )
}
